import { Stack, Typography } from '@mui/material';
import { StatusChip } from '@/components/StatusChip';
import { formatQuotationDate } from '@/features/operations/labels';
import type { QuotationConfirmationEmail } from '@/features/operations/types';

/**
 * Whether the applicant got their confirmation email (#163).
 *
 * Read-only, deliberately. The queue already retries on its own, so a button here would be a
 * second way to send the same message, with its own authorization and its own abuse limit to
 * think about. What whoever answers the phone needs is the answer to "did we email you": that
 * is this line.
 *
 * Renders nothing for a request from before the email existed. Saying "no enviado" about one
 * would read as a failure, when in fact nothing was ever attempted.
 */
export function ConfirmationEmailStatus({
  confirmationEmail,
}: {
  confirmationEmail: QuotationConfirmationEmail | null;
}) {
  if (!confirmationEmail) {
    return null;
  }

  const sentOn =
    confirmationEmail.status === 'SENT' && confirmationEmail.sentAt
      ? ` el ${formatQuotationDate(confirmationEmail.sentAt)}`
      : '';
  // The provider's own message, shown as it came. The same choice the branch screen makes with
  // a rejected deactivation: a paraphrase of an error nobody here predicted is worse than the
  // error.
  const reason =
    confirmationEmail.status === 'FAILED' && confirmationEmail.error
      ? ` · ${confirmationEmail.error}`
      : '';
  const attempts =
    confirmationEmail.status === 'FAILED' && confirmationEmail.attempts > 1
      ? ` tras ${confirmationEmail.attempts} intentos`
      : '';
  // Said out loud, because "enviado" would otherwise be a lie in every environment that has
  // not configured a real provider -- which today is all of them. Somebody reading this line
  // in a demo must not be able to conclude the applicant received anything.
  const fakeTransport =
    confirmationEmail.transport === 'console'
      ? ' · transporte de prueba, no se envió de verdad'
      : '';

  // The state is the chip, in the color of its family (#285); the details stay as text.
  const details = `${sentOn}${attempts}${reason}${fakeTransport}`;

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ flexWrap: 'wrap', rowGap: 0.5 }}
      data-section="confirmation-email"
    >
      <Typography variant="body2" color="text.secondary">
        Correo de confirmación:
      </Typography>
      <StatusChip domain="emailDelivery" status={confirmationEmail.status} />
      {details ? (
        <Typography
          variant="body2"
          color={confirmationEmail.status === 'FAILED' ? 'error.main' : 'text.secondary'}
        >
          {details}
        </Typography>
      ) : null}
    </Stack>
  );
}
